// ═══════════════════════════════════════════════════════════
//  useLaunchpad.js  —  WebRTC engine.
//  Signaling: our realtime layer's Presence + Broadcast channels
//  (backed by SSE + LISTEN/NOTIFY server-side).  No separate
//  signaling server required.
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef, useState, useCallback } from 'react';
import { db } from '../db';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const SPEAKING_THRESHOLD = 0.015;

// ─────────────────────────────────────────────────────────
export function useLaunchpad({ sessionId, user }) {
  const [peers, setPeers]             = useState([]);
  const [localStream, setLocalStream] = useState(null);
  const [micOn, setMicOn]             = useState(true);
  const [camOn, setCamOn]             = useState(true);
  const [speaking, setSpeaking]       = useState(false);
  const [status, setStatus]           = useState('idle'); // idle | joining | live | ended
  const [isSharing, setIsSharing]     = useState(false);

  const channelRef   = useRef(null);
  const pcsRef       = useRef(new Map());   // peerId → RTCPeerConnection
  const streamRef    = useRef(null);
  const audioCtxRef  = useRef(null);
  const rafRef       = useRef(null);
  const screenStreamRef = useRef(null);

  // Keep stable refs to avoid stale closures in async WebRTC callbacks
  const userRef      = useRef(user);
  const sendSignalRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  // ── Helpers ─────────────────────────────────────────────
  const getInitials = (name = '') =>
    name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const colorFromId = (id = '') => {
    const colors = ['#E87722','#FF3CAC','#7B2FFF','#FFB830','#FF6B6B','#3CF0FF'];
    let h = 0;
    for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h);
    return colors[Math.abs(h) % colors.length];
  };

  // ── Send signal via realtime broadcast ─────────────────
  const sendSignal = useCallback((payload) => {
    channelRef.current?.send({
      type:    'broadcast',
      event:   'signal',
      payload: { ...payload, from: userRef.current?.id },
    });
  }, []);

  // Keep ref in sync for use inside async callbacks
  useEffect(() => { sendSignalRef.current = sendSignal; }, [sendSignal]);

  // ── Remove peer ──────────────────────────────────────────
  const removePeer = useCallback((peerId) => {
    const pc = pcsRef.current.get(peerId);
    if (pc) { pc.close(); pcsRef.current.delete(peerId); }
    setPeers(prev => prev.filter(p => p.id !== peerId));
  }, []);

  // ── Create RTCPeerConnection ─────────────────────────────
  const createPeerConnection = useCallback((peerId, peerName) => {
    if (pcsRef.current.has(peerId)) return pcsRef.current.get(peerId);

    const pc = new RTCPeerConnection({
      iceServers: ICE_SERVERS,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      iceCandidatePoolSize: 10,
    });

    pcsRef.current.set(peerId, pc);

    // Add local tracks
    streamRef.current?.getTracks().forEach(track => {
      pc.addTrack(track, streamRef.current);
    });

    // ICE candidates → broadcast to peer
    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        sendSignalRef.current?.({ type: 'ice', to: peerId, candidate });
      }
    };

    // Remote tracks
    const remoteStream = new MediaStream();
    pc.ontrack = ({ track }) => {
      remoteStream.addTrack(track);
      setPeers(prev => prev.map(p =>
        p.id === peerId ? { ...p, stream: remoteStream } : p
      ));
    };

    // Connection state
    pc.onconnectionstatechange = () => {
      if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) {
        removePeer(peerId);
      }
    };

    // Add peer placeholder
    setPeers(prev => {
      if (prev.find(p => p.id === peerId)) return prev;
      return [...prev, {
        id:       peerId,
        name:     peerName || 'Peer',
        initials: getInitials(peerName || 'Peer'),
        color:    colorFromId(peerId),
        stream:   remoteStream,
        speaking: false,
        micOn:    true,
        camOn:    true,
      }];
    });

    return pc;
  }, [removePeer]);

  // ── SDP codec preference ─────────────────────────────────
  const preferCodec = (sdp, type, codecName) => {
    const lines = sdp.split('\r\n');
    const mIdx  = lines.findIndex(l => l.startsWith(`m=${type}`));
    if (mIdx === -1) return sdp;
    const re  = new RegExp(`a=rtpmap:(\\d+) ${codecName}`, 'i');
    let pt = null;
    for (const l of lines) { const m = l.match(re); if (m) { pt = m[1]; break; } }
    if (!pt) return sdp;
    const parts = lines[mIdx].split(' ');
    const rest  = parts.slice(3).filter(p => p !== pt);
    lines[mIdx] = [...parts.slice(0, 3), pt, ...rest].join(' ');
    return lines.join('\r\n');
  };

  // ── Handle incoming signals ──────────────────────────────
  const handleSignal = useCallback(async (msg) => {
    // Ignore messages not addressed to us
    if (msg.to && msg.to !== userRef.current?.id) return;

    switch (msg.type) {
      case 'offer': {
        const pc = createPeerConnection(msg.from, msg.senderName);
        await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        const answer = await pc.createAnswer();
        answer.sdp = preferCodec(answer.sdp, 'video', 'VP9');
        await pc.setLocalDescription(answer);
        sendSignalRef.current?.({
          type:       'answer',
          to:         msg.from,
          sdp:        answer,
          senderName: userRef.current?.name,
        });
        break;
      }

      case 'answer': {
        const pc = pcsRef.current.get(msg.from);
        if (pc) await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        break;
      }

      case 'ice': {
        const pc = pcsRef.current.get(msg.from);
        if (pc && msg.candidate) {
          try { await pc.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch (_) {}
        }
        break;
      }

      default: break;
    }
  }, [createPeerConnection]);

  // ── Audio speaking detection ─────────────────────────────
  const startAudioAnalysis = (stream) => {
    try {
      const AudioCtx = window.AudioContext || window['webkitAudioContext'];
      const ctx      = new AudioCtx();
      const source   = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      audioCtxRef.current = ctx;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const level = data.reduce((a, b) => a + b, 0) / data.length / 255;
        setSpeaking(level > SPEAKING_THRESHOLD);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      console.warn('[LAUNCHPAD] Audio analysis failed:', e.message);
    }
  };

  // ── Local media ──────────────────────────────────────────
  const startMedia = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width:       { ideal: 1280, min: 640 },
          height:      { ideal: 720,  min: 480 },
          frameRate:   { ideal: 30,   min: 15  },
          facingMode:  'user',
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl:  true,
          sampleRate: 48000,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      setLocalStream(stream);
      startAudioAnalysis(stream);
      return stream;
    } catch (e) {
      console.warn('[LAUNCHPAD] Media access failed:', e.message);
      const empty = new MediaStream();
      streamRef.current = empty;
      setLocalStream(empty);
      return empty;
    }
  }, []);

  // ── Public controls ──────────────────────────────────────
  const toggleMic = useCallback(() => {
    setMicOn(prev => {
      const next = !prev;
      streamRef.current?.getAudioTracks().forEach(t => { t.enabled = next; });
      return next;
    });
  }, []);

  const toggleCam = useCallback(() => {
    setCamOn(prev => {
      const next = !prev;
      streamRef.current?.getVideoTracks().forEach(t => { t.enabled = next; });
      return next;
    });
  }, []);

  // ── Screen share ─────────────────────────────────────────
  const stopScreenShare = useCallback(() => {
    screenStreamRef.current?.getTracks().forEach(t => t.stop());
    screenStreamRef.current = null;
    setIsSharing(false);

    // Restore camera track to all peer connections
    const camTrack = streamRef.current?.getVideoTracks()[0];
    if (camTrack) {
      pcsRef.current.forEach(pc => {
        const sender = pc.getSenders().find(s => s.track?.kind === 'video');
        if (sender) sender.replaceTrack(camTrack);
      });
    }
  }, []);

  const stopScreenShareRef = useRef(stopScreenShare);
  useEffect(() => { stopScreenShareRef.current = stopScreenShare; }, [stopScreenShare]);

  const shareScreen = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always', displaySurface: 'monitor' },
        audio: false,
      });
      const screenTrack = stream.getVideoTracks()[0];
      screenStreamRef.current = stream;
      setIsSharing(true);

      // Replace video sender in all peer connections
      pcsRef.current.forEach(pc => {
        const sender = pc.getSenders().find(s => s.track?.kind === 'video');
        if (sender) sender.replaceTrack(screenTrack);
      });

      // Auto-stop when user clicks browser's "Stop sharing"
      screenTrack.onended = () => stopScreenShareRef.current();
    } catch (e) {
      if (e.name !== 'NotAllowedError') {
        console.warn('[LAUNCHPAD] Screen share failed:', e.message);
      }
    }
  }, []);

  // ── Join ─────────────────────────────────────────────────
  const join = useCallback(async () => {
    if (!sessionId || !user?.id) {
      console.error('[LAUNCHPAD] sessionId and user.id required');
      return;
    }
    if (status === 'live' || status === 'joining') return;

    setStatus('joining');

    await startMedia();

    const channel = db.channel(`launchpad:${sessionId}`, {
      config: {
        broadcast: { self: false, ack: false },
        presence:  { key: user.id },
      },
    });

    channelRef.current = channel;

    // New peer joined → we are the initiator, send them an offer
    channel.on('presence', { event: 'join' }, async ({ key, newPresences }) => {
      if (key === user.id) return; // skip self
      const meta     = newPresences[0] || {};
      const peerName = meta.name || 'Peer';

      const pc    = createPeerConnection(key, peerName);
      const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
      offer.sdp   = preferCodec(offer.sdp, 'video', 'VP9');
      offer.sdp   = preferCodec(offer.sdp, 'audio', 'opus');
      await pc.setLocalDescription(offer);

      sendSignalRef.current?.({
        type:       'offer',
        to:         key,
        sdp:        offer,
        senderName: user.name,
      });
    });

    // Peer left
    channel.on('presence', { event: 'leave' }, ({ key }) => {
      removePeer(key);
    });

    // WebRTC signaling messages
    channel.on('broadcast', { event: 'signal' }, ({ payload }) => {
      handleSignal(payload);
    });

    // Subscribe and track our presence
    channel.subscribe(async (state) => {
      if (state === 'SUBSCRIBED') {
        await channel.track({ name: user.name, email: user.email });
        setStatus('live');
      }
    });
  }, [sessionId, user, status, startMedia, createPeerConnection, removePeer, handleSignal]);

  // ── Leave ─────────────────────────────────────────────────
  const leave = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    pcsRef.current.forEach(pc => pc.close());
    pcsRef.current.clear();
    audioCtxRef.current?.close();
    screenStreamRef.current?.getTracks().forEach(t => t.stop());
    screenStreamRef.current = null;
    setIsSharing(false);
    channelRef.current?.unsubscribe();
    channelRef.current = null;
    streamRef.current  = null;
    audioCtxRef.current = null;
    setLocalStream(null);
    setPeers([]);
    setSpeaking(false);
    setMicOn(true);
    setCamOn(true);
    setStatus('idle');
  }, []);

  // Cleanup on unmount only. We intentionally exclude `leave` from deps:
  // it's a stable useCallback and we don't want to run cleanup on every
  // re-creation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => leave(), []);

  return {
    localStream,
    peers,
    micOn,
    camOn,
    speaking,
    status,
    isSharing,
    join,
    leave,
    toggleMic,
    toggleCam,
    shareScreen,
    stopScreenShare,
  };
}
